/**
 * Landing page — marketing-ready entry point for buyers.
 *
 * Hero + feature grid + how-it-works + CTA. No API key required to view.
 * Links to /demo (the Home page) for the live demo.
 */

import { Link } from "react-router-dom";
import {
  BarChart3,
  Brain,
  FileText,
  Lock,
  Layers,
  Shield,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";

import { useTheme } from "@/contexts/ThemeContext";
import { Credit } from "@/components/Credit";

const FEATURES = [
  {
    icon: Brain,
    title: "Seven-Signal ML Scoring",
    desc: "Semantic, keyword, bullet, section, title, seniority, and field signals fused into a single score.",
  },
  {
    icon: Layers,
    title: "Two-Stage Retrieval",
    desc: "Bi-encoder for fast candidate screening, cross-encoder for precision reranking of top matches.",
  },
  {
    icon: Sparkles,
    title: "AI Evidence Extraction",
    desc: "LLM-powered bullet-level evidence with strengths, gaps, and transferable skill detection.",
  },
  {
    icon: Shield,
    title: "Bias Audit Built In",
    desc: "PII masking pipeline runs every match twice. Delta > 10 points is auto-flagged for human review.",
  },
  {
    icon: Users,
    title: "Bulk Ranking",
    desc: "Rank up to 20 candidates against one JD in parallel with live progress streaming.",
  },
  {
    icon: FileText,
    title: "Compliance PDF Reports",
    desc: "One-click export for bias audits, calibration plots, and per-candidate breakdowns.",
  },
  {
    icon: Lock,
    title: "Multi-Tenant Ready",
    desc: "Row-level tenant isolation, per-tenant theming, and usage metering from day one.",
  },
  {
    icon: BarChart3,
    title: "Benchmark Validated",
    desc: "CI-gated evaluation harness with nDCG@5, Precision@k, MRR, and Spearman metrics.",
  },
  {
    icon: Zap,
    title: "White-Label in Minutes",
    desc: "CSS custom properties for colors, logo, and branding. Your brand, our engine.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Upload or paste",
    desc: "Drop a resume and job description — PDF, DOCX, or plain text.",
  },
  {
    num: "02",
    title: "Instant ML scoring",
    desc: "Seven independent signals score the match in under a second.",
  },
  {
    num: "03",
    title: "AI evidence review",
    desc: "The LLM extracts bullet-level strengths, gaps, and rationale.",
  },
  {
    num: "04",
    title: "Bias check + export",
    desc: "Automatic PII audit flags identity influence. Export compliance PDFs.",
  },
];

export default function Landing() {
  const theme = useTheme();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/60 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {theme.logoUrl ? (
              <img src={theme.logoUrl} alt={theme.productName} className="h-8 w-auto" />
            ) : (
              <span className="text-2xl font-extrabold ta-gradient-text tracking-tight">
                {theme.productName}
              </span>
            )}
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/docs"
              className="text-sm text-slate-400 hover:text-slate-100 transition hidden sm:block"
            >
              How it works
            </Link>
            <Link
              to="/demo"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all"
            >
              Try the demo
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 sm:pt-32 pb-16 text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest bg-blue-500/10 border border-blue-500/30 text-blue-300 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_#60a5fa] animate-pulse" />
          AI-native resume matching
        </span>
        <h1 className="text-4xl sm:text-6xl font-extrabold leading-tight tracking-tight ta-gradient-text mb-6">
          Match Resumes to Jobs
          <br />
          with ML Precision.
        </h1>
        <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-300 leading-relaxed mb-10">
          Seven independent ML scoring signals, AI-powered evidence extraction,
          built-in bias audits, and compliance reporting.
          Ready to white-label.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link
            to="/demo"
            className="px-8 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white text-lg font-bold shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 transition-all"
          >
            Launch demo
          </Link>
          <Link
            to="/docs"
            className="px-8 py-4 rounded-xl border border-slate-700/60 text-slate-300 hover:text-white hover:border-slate-600 transition text-lg font-semibold"
          >
            How it works
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-100 mb-4">
          Everything you need to match at scale
        </h2>
        <p className="text-center text-slate-400 mb-12 max-w-2xl mx-auto">
          From single-candidate analysis to batch ranking across entire pools,
          every feature is production-ready and bias-aware by design.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="ta-card p-6 hover:border-blue-500/30 transition group"
            >
              <f.icon className="w-8 h-8 text-blue-400 mb-4 group-hover:text-blue-300 transition" />
              <h3 className="text-base font-semibold text-slate-100 mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-100 mb-12">
          How it works
        </h2>
        <div className="grid sm:grid-cols-2 gap-8">
          {STEPS.map((s) => (
            <div key={s.num} className="flex gap-4">
              <div className="text-3xl font-extrabold ta-gradient-text shrink-0 w-12">
                {s.num}
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-100 mb-1">
                  {s.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
        <div className="ta-card p-10 sm:p-14">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-4">
            Ready to see it in action?
          </h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            No sign-up required. Bring your own Groq API key (free tier available)
            and match your first resume in seconds.
          </p>
          <Link
            to="/demo"
            className="inline-block px-10 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white text-lg font-bold shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 transition-all"
          >
            Try the live demo
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60 mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-center">
          {theme.showAnalyticaCredit && (
            <Credit tier={3} className="text-slate-400" />
          )}
          <div className="mt-4 text-xs text-slate-600">
            {theme.footerTagline}
          </div>
        </div>
      </footer>
    </div>
  );
}
